/**
 * useNFTOperations - Custom hook for NFT sale and cancel operations
 * Handles blockchain transactions with proper error handling
 */

import { useState, useCallback } from 'react';
import { fundRaisin } from '../contracts/fundraisin';
import { parseErrorMessage } from '../utils/errorUtils';
import { logError, logInfo } from '../utils/log';

export const useNFTOperations = (
  { siteDetails, themeConfig, isWalletConnected, connectWallet, refreshNftData },
  { setError, closeSaleModal }
) => {
  const [spinner, setSpinner] = useState(false);

  const cancelSale = useCallback(async (card) => {
    setSpinner(true);
    
    try {
      const { tokenId, siteTokenIndex, originalPrice } = card;
      const price = originalPrice.toString();
      
      let status = isWalletConnected;
      if (!isWalletConnected) {
        status = await connectWallet();
      }

      if (status) {
        await fundRaisin.changeTokenPrice(
          siteDetails.siteId,
          siteTokenIndex,
          tokenId,
          price,
          false
        );
        
        logInfo('Sale cancelled successfully');
        closeSaleModal();
        refreshNftData();
      }
    } catch (err) {
      logError('Cancel sale error:', err);
      const errorMsg = parseErrorMessage(err, themeConfig, 'cancel sale');
      setError(errorMsg);
    } finally {
      setSpinner(false);
    }
  }, [
    siteDetails.siteId,
    isWalletConnected,
    connectWallet,
    refreshNftData,
    closeSaleModal,
    setError,
    themeConfig
  ]);

  const placeSale = useCallback(async (card, priceDist) => {
    setSpinner(true);
    
    try {
      const { tokenId, siteTokenIndex } = card;
      const price = priceDist.toString();
      
      let status = isWalletConnected;
      if (!isWalletConnected) {
        status = await connectWallet();
      }

      if (status) {
        await fundRaisin.changeTokenPrice(
          siteDetails.siteId,
          siteTokenIndex,
          tokenId,
          price,
          true
        );
        
        logInfo('Sale placed successfully');
        closeSaleModal();
        refreshNftData();
      }
    } catch (err) {
      logError('Place sale error:', err);
      const errorMsg = parseErrorMessage(err, themeConfig, 'sell');
      setError(errorMsg);
    } finally {
      setSpinner(false);
    }
  }, [
    siteDetails.siteId,
    isWalletConnected,
    connectWallet,
    refreshNftData,
    closeSaleModal,
    setError,
    themeConfig
  ]);

  return {
    spinner,
    cancelSale,
    placeSale,
  };
};
